import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useCompanyLogo } from "@/hooks/use-company-logo";
import { LayoutDashboard, Users, ShoppingCart, LogOut, Menu, X, Settings, ClipboardList } from "lucide-react";
import { useState } from "react";

interface SellerSession {
  id: string;
  name: string;
  canRegisterClients: boolean;
  canCreateOrders: boolean;
  canViewOrders: boolean;
}

interface SellerLayoutProps {
  children: React.ReactNode;
}

const allNavItems = [
  { href: "/seller", label: "Översikt", icon: LayoutDashboard, permission: null },
  { href: "/seller/clients", label: "Kunder", icon: Users, permission: null },
  { href: "/seller/new-order", label: "Ny order", icon: ShoppingCart, permission: "canCreateOrders" as const },
  { href: "/seller/orders", label: "Ordrar", icon: ClipboardList, permission: "canViewOrders" as const },
  { href: "/seller/settings", label: "Inställningar", icon: Settings, permission: null },
];

export function useSellerSession() {
  return useQuery<SellerSession | null>({
    queryKey: ["/api/seller-auth/me"],
    staleTime: 30_000,
  });
}

export default function SellerLayout({ children }: SellerLayoutProps) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { toast } = useToast();
  const logoUrl = useCompanyLogo();
  const { data: seller } = useSellerSession();

  const handleLogout = async () => {
    await apiRequest("POST", "/api/seller-auth/logout", {});
    queryClient.clear();
    window.location.replace("/seller");
  };

  const visibleNavItems = allNavItems.filter(item => {
    if (!item.permission) return true;
    return seller ? seller[item.permission] : false;
  });

  const NavLinks = () => (
    <>
      {visibleNavItems.map((item) => {
        const Icon = item.icon;
        const isActive = location === item.href || (item.href !== "/seller" && location.startsWith(item.href));
        return (
          <Link key={item.href} href={item.href}>
            <a
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "hover:bg-accent hover:text-accent-foreground"
              }`}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </a>
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="button-mobile-menu">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <img src={logoUrl} alt="Rätt Order" className="h-8 w-auto object-contain" />
            <span className="font-semibold text-sm hidden sm:block">Säljportal</span>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            <NavLinks />
          </nav>

          <div className="flex items-center gap-2">
            {seller && <span className="text-sm text-muted-foreground hidden sm:block">{seller.name}</span>}
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="button-seller-logout">
              <LogOut className="h-4 w-4 mr-1" />
              Logga ut
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden border-b bg-card px-4 pb-3 space-y-1">
          <NavLinks />
        </div>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
}
